// THE BORE VOLUME: the cut volumes of one bore, the ONE function the drawn
// terrain cut (client/src/borecut.js cutTile, re-exported from there) and the
// felt skin (world/emit.js, P-1c: the spans carve footprint is OUTSIDE EVERY
// CUT VOLUME OF EVERY BORE (depthInAll over the union)) both read. PURE
// module, dependency-free (no three,
// no DOM, no imports); CommonJS like game/lodmath.js: esbuild / ESM import in
// the client and the gates, require in world/.
//
// Per drawn segment (station i -> i+1) the convex hull of the two station
// rings: the arch points at floor + hAt(o) and the wall foot corners at
// floor + cf o - FLOOR_UNDER_M, placed on the station normal at the
// (bend-clamped) plan offset; at a portal the end station's section line is
// the portal plane. Plus the record's rooms (bores[k].cut.rooms: a hall's air,
// a chamber), each the convex hull of its points (seg = -1 - r) — a room at an
// end can reach past the end ring's plane. Each volume is a short list of
// half-spaces f(x, y, z) <= 0 (vertical ones first); depthIn > 0 = inside.
// The station rings come from ways.json bores[k].cut (the prism list emit
// lined) or are derived from the bore's own stations + section (+ section.top).
// Moved from client/src/borecut.js (CUT-8: the code verbatim, `export` turned
// into module.exports); scripts/check-bore-cut.mjs measures it.
'use strict';

// the volume's floor runs this far under the drawn floor (the old discard's
// floor - 0.1: terrain flush with the slab never z-fights it)
const FLOOR_UNDER_M = 0.1;
// The terrain cut removes the volume grown by CLEAR_M: the drawn attributes are float32
// (aH: half an ulp is 0.06 mm under 2048 m and 0.12 mm above; tile-local
// xz <= 0.12 mm; so <= ~0.21 mm on a unit-normal plane), so a kept
// vertex rounded onto the volume side still ends outside it — the gate
// measures penetration EXACTLY (0 m) on the stored float32 values.
const CLEAR_M = 0.0005;

// ------------------------------------------------------------ the rings
// station normals: = world/flights.js stationNormals (the emit's rule, every
// lining record stands on them). check-bore-cut asserts equality on a bake.
function stationNormals(x, z) {
  const n = x.length, nx = new Float64Array(n), nz = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const q = Math.min(n - 1, i + 1), o = Math.max(0, i - 1);
    const dx = x[q] - x[o], dz = z[q] - z[o];
    const len = Math.hypot(dx, dz);
    if (len < 0.5 && i > 0) { nx[i] = nx[i - 1]; nz[i] = nz[i - 1]; continue; }
    const l = len || 1;
    nx[i] = -dz / l; nz[i] = dx / l;
  }
  return { nx, nz };
}

// the profile's lateral sequence -half .. +half: [[o, h], ...] (= emit mirrored)
function mirrored(prof) {
  const out = [];
  for (let i = 0; i < prof.length; i++) out.push([-prof[i][0], prof[i][1]]);
  for (let i = prof.length - 2; i >= 0; i--) out.push([prof[i][0], prof[i][1]]);
  return out;
}

// one ring's arch over its floor: the mirrored profile lifted by `lift` and
// capped at `cap`, the cap's crossings inserted (so the ring is exact)
function ringArch(lat, lift, cap) {
  const out = [];
  for (let k = 0; k < lat.length; k++) {
    const [o, h0] = lat[k];
    const h = h0 + lift;
    if (k > 0) {
      const [po, ph0] = lat[k - 1];
      const ph = ph0 + lift;
      if ((ph - cap) * (h - cap) < 0) {
        const t = (cap - ph) / (h - ph);
        out.push([po + (o - po) * t, cap]);
      }
    }
    out.push([o, Math.min(h, cap)]);
  }
  return out;
}

// The drawn ring stations of one registered bore run, normalised:
// { x, z, floor, nx, nz, cf, lo, hi, lift, cap, lat, rooms }.
// run = ways2.js _bores: { prof, pts: [[x, floor, z]], top?, cut? }.
// (P2a, APP_ARCHITECT §16) a SITE run (ways2.js _bores 'sites2', world/emit.js
// siteBuild: { site: true, cut: { rooms } }) has no drawn stations: its cut is
// its rooms alone, each a room volume like a bore's (same shape, from 'cut').
function boreStations(run) {
  if (run.site) {
    const rooms = run.cut && Array.isArray(run.cut.rooms) ? run.cut.rooms : [];
    return { x: [], z: [], floor: [], nx: [], nz: [], cf: [], lo: [], hi: [], lift: [], cap: [], lat: [], half: 0, rooms, from: 'cut' };
  }
  const prof = run.prof;
  const half = prof[0][0], crown = prof[prof.length - 1][1];
  const lat = mirrored(prof);
  const c = run.cut;
  // the record (x, z, floor per station, same length) or the derived path
  if (c && c.v === 1 && Array.isArray(c.x) && c.x.length >= 2 && Array.isArray(c.z) && c.z.length === c.x.length
    && Array.isArray(c.floor) && c.floor.length === c.x.length) {
    const n = c.x.length;
    const arr = (k, d) => (Array.isArray(c[k]) && c[k].length === n ? c[k] : new Array(n).fill(d));
    let nx = c.nx, nz = c.nz;
    if (!(Array.isArray(nx) && nx.length === n && Array.isArray(nz) && nz.length === n)) ({ nx, nz } = stationNormals(c.x, c.z));
    // no `cap`: the roof is section.top where it is per record station (a
    // capped underpass is never over-cut), else the crown — never unbounded
    let cap = Array.isArray(c.cap) && c.cap.length === n ? c.cap : null, lift = Array.isArray(c.lift) && c.lift.length === n ? c.lift : null;
    if (!cap) {
      const top = Array.isArray(run.top) && run.top.length === n ? run.top : null;
      cap = new Array(n);
      for (let i = 0; i < n; i++) cap[i] = top && Number.isFinite(top[i]) ? top[i] : crown;
    }
    if (!lift) lift = cap.map((t) => Math.max(0, t - crown));
    return { x: c.x, z: c.z, floor: c.floor, nx, nz, cf: arr('cf', 0), lo: arr('lo', half),
      hi: arr('hi', half), lift, cap, lat, half,
      rooms: Array.isArray(c.rooms) ? c.rooms : [], from: 'cut' };
  }
  const n = run.pts.length;
  const x = run.pts.map((p) => p[0]), z = run.pts.map((p) => p[2]), floor = run.pts.map((p) => p[1]);
  const { nx, nz } = stationNormals(x, z);
  const top = Array.isArray(run.top) && run.top.length === n ? run.top : null;
  const cap = new Array(n), lift = new Array(n);
  for (let i = 0; i < n; i++) {
    const t = top && Number.isFinite(top[i]) ? top[i] : crown;
    cap[i] = t; lift[i] = Math.max(0, t - crown);
  }
  return { x, z, floor, nx, nz, cf: new Array(n).fill(0), lo: new Array(n).fill(half),
    hi: new Array(n).fill(half), lift, cap, lat, half, rooms: [], from: 'derived' };
}

// station i's ring: { top: [[x, y, z], ...] arch points, foot: the two wall
// foot corners [[x, y, z] left, right] }
function ringOf(S, i) {
  const co = (o) => (o < 0 ? -Math.min(-o, S.lo[i]) : Math.min(o, S.hi[i]));
  const x = S.x[i], z = S.z[i], f = S.floor[i], nx = S.nx[i], nz = S.nz[i];
  const top = ringArch(S.lat, S.lift[i], S.cap[i]).map(([o, h]) => {
    const c = co(o);
    return [x + nx * c, f + h, z + nz * c];
  });
  const foot = [-S.half, S.half].map((o) => {
    const c = co(o);
    return [x + nx * c, f + S.cf[i] * c - FLOOR_UNDER_M, z + nz * c];
  });
  return { top, foot };
}

// ------------------------------------------------------------ hull planes
// 2-D convex hull (monotone chain) of [[x, z]], counter-clockwise
function hull2(pts) {
  const p = pts.slice().sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cr = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower = [], upper = [];
  for (const q of p) {
    while (lower.length >= 2 && cr(lower[lower.length - 2], lower[lower.length - 1], q) <= 1e-12) lower.pop();
    lower.push(q);
  }
  for (let k = p.length - 1; k >= 0; k--) {
    const q = p[k];
    while (upper.length >= 2 && cr(upper[upper.length - 2], upper[upper.length - 1], q) <= 1e-12) upper.pop();
    upper.push(q);
  }
  upper.pop(); lower.pop();
  return lower.concat(upper);
}

// plane y = a x + b z + c through three points (relative to origin o), or
// null when they are collinear in plan
function planeY(p, q, r, ox, oy, oz) {
  const x1 = q[0] - p[0], z1 = q[2] - p[2], y1 = q[1] - p[1];
  const x2 = r[0] - p[0], z2 = r[2] - p[2], y2 = r[1] - p[1];
  const det = x1 * z2 - x2 * z1;
  if (Math.abs(det) < 1e-9) return null;
  const a = (y1 * z2 - y2 * z1) / det, b = (x1 * y2 - x2 * y1) / det;
  const c = (p[1] - oy) - a * (p[0] - ox) - b * (p[2] - oz);
  return [a, b, c];
}

// supporting planes of one side of the hull of `pts` (upper: every point on
// or under; lower: on or over), from the candidate triples `tri`
function sidePlanes(pts, triples, upper, ox, oy, oz) {
  const out = [];
  for (const [i, j, k] of triples) {
    const pl = planeY(pts[i], pts[j], pts[k], ox, oy, oz);
    if (!pl) continue;
    const [a, b, c] = pl;
    let ok = true;
    for (const s of pts) {
      const d = (s[1] - oy) - (a * (s[0] - ox) + b * (s[2] - oz) + c);
      if (upper ? d > 1e-7 : d < -1e-7) { ok = false; break; }
    }
    if (!ok) continue;
    if (out.some((q) => Math.abs(q[0] - a) < 1e-9 && Math.abs(q[1] - b) < 1e-9 && Math.abs(q[2] - c) < 1e-7)) continue;
    out.push(pl);
  }
  return out;
}

// Volume from a point set and its candidate triples (upper and lower).
// Half-spaces are signed distances: f = A x + B z + C y + D, inside f <= 0.
function makeVolume(pts, upTriples, loTriples, tag, ringOf = null) {
  let ox = 0, oy = 0, oz = 0;
  for (const p of pts) { ox += p[0]; oy += p[1]; oz += p[2]; }
  ox /= pts.length; oy /= pts.length; oz /= pts.length;
  const H = hull2(pts.map((p, i) => [p[0], p[2], i]));
  if (H.length < 3) return null;
  const P = [], late = [];
  let cx = 0, cz = 0;
  for (const q of H) { cx += q[0]; cz += q[1]; }
  cx /= H.length; cz /= H.length;
  for (let k = 0; k < H.length; k++) {
    const p = H[k], q = H[(k + 1) % H.length];
    const ex = q[0] - p[0], ez = q[1] - p[1], L = Math.hypot(ex, ez);
    if (L < 1e-9) continue;
    let A = ez / L, B = -ex / L, D = -(A * p[0] + B * p[1]);
    if (A * cx + B * cz + D > 0) { A = -A; B = -B; D = -D; }
    // a SECTION line (both ends on one station ring) is subtracted LAST:
    // first the wall lines, floor and arch carve the tube's own band, so a
    // section line only splits what is inside that band — a coarse
    // triangle over a long tube is not sliced across its whole width at
    // every station
    if (ringOf && ringOf(p[2]) === ringOf(q[2])) late.push(A, B, 0, D); else P.push(A, B, 0, D);
  }
  const nv = P.length / 4;
  // floor: y >= a x + b z + c  ->  (a x + b z + c - y) / n <= 0
  const lo = sidePlanes(pts, loTriples, false, ox, oy, oz);
  for (const [a, b, c] of lo) {
    const n = Math.hypot(a, b, 1);
    P.push(a / n, b / n, -1 / n, (c + oy - a * ox - b * oz) / n);
  }
  // arch: y <= a x + b z + c  ->  (y - a x - b z - c) / n <= 0
  const up = sidePlanes(pts, upTriples, true, ox, oy, oz);
  for (const [a, b, c] of up) {
    const n = Math.hypot(a, b, 1);
    P.push(-a / n, -b / n, 1 / n, -(c + oy - a * ox - b * oz) / n);
  }
  if (!lo.length || !up.length) return null;
  // the footprint half-spaces (plan tests): the early ones and the late ones
  const vk = [];
  for (let k = 0; k < nv; k++) vk.push(k);
  for (let k = 0; k < late.length / 4; k++) vk.push(P.length / 4 + k);
  P.push(...late);
  let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity, y0 = Infinity, y1 = -Infinity;
  for (const p of pts) {
    if (p[0] < x0) x0 = p[0]; if (p[0] > x1) x1 = p[0];
    if (p[2] < z0) z0 = p[2]; if (p[2] > z1) z1 = p[2];
    if (p[1] < y0) y0 = p[1]; if (p[1] > y1) y1 = p[1];
  }
  return { x0, x1, z0, z1, y0, y1, P: Float64Array.from(P), nk: P.length / 4, vk: Int32Array.from(vk), pts, ...tag };
}

// all triples of a point set (rooms)
function allTriples(n) {
  const t = [];
  for (let i = 0; i < n; i++) for (let j = i + 1; j < n; j++) for (let k = j + 1; k < n; k++) t.push([i, j, k]);
  return t;
}
// the consecutive vertices of a ring's UPPER hull in its own section plane
// (s = signed plan offset along the station normal, y): [[i, j], ...]
function upperEdges(top, cx, cz, nx, nz) {
  const q = top.map((p, i) => [(p[0] - cx) * nx + (p[2] - cz) * nz, p[1], i])
    .sort((a, b) => a[0] - b[0] || b[1] - a[1]);
  const h = [];
  for (const p of q) {
    // a point stacked under the kept one (a bend-clamped wall) is no vertex
    if (h.length && Math.abs(h[h.length - 1][0] - p[0]) < 1e-9) continue;
    while (h.length >= 2) {
      const o = h[h.length - 2], a = h[h.length - 1];
      if ((a[0] - o[0]) * (p[1] - o[1]) - (a[1] - o[1]) * (p[0] - o[0]) >= -1e-12) h.pop(); else break;
    }
    h.push(p);
  }
  const e = [];
  for (let k = 0; k + 1 < h.length; k++) e.push([h[k][2], h[k + 1][2]]);
  return e;
}

// Every volume of one registered bore run: one per drawn segment, plus its
// rooms. `bore` tags them (the gate's residue table).
function boreVolumes(run, bore = 0) {
  const S = boreStations(run);
  const out = [];
  const n = S.x.length;
  let prev = n ? ringOf(S, 0) : null;
  for (let i = 0; i + 1 < n; i++) {
    const next = ringOf(S, i + 1);
    if (Math.hypot(S.x[i + 1] - S.x[i], S.z[i + 1] - S.z[i]) > 1e-4) {
      // ring order: [a's arch..., a's feet, b's arch..., b's feet]
      const A = prev.top.concat(prev.foot), B = next.top.concat(next.foot);
      const pts = A.concat(B);
      const na = A.length, ta = prev.top.length, tb = next.top.length;
      // upper hull = the arch. Each ring lies in its own vertical section
      // plane, so a non-vertical face meets one ring in an edge of THAT
      // ring's upper hull and holds a point of the other: these triples are
      // complete. Lower hull = the four wall foot corners.
      const upT = [];
      for (const [i0, j0] of upperEdges(prev.top, S.x[i], S.z[i], S.nx[i], S.nz[i])) {
        for (let k = 0; k < tb; k++) upT.push([i0, j0, na + k]);
      }
      for (const [i0, j0] of upperEdges(next.top, S.x[i + 1], S.z[i + 1], S.nx[i + 1], S.nz[i + 1])) {
        for (let k = 0; k < ta; k++) upT.push([k, na + i0, na + j0]);
      }
      const loT = [[ta, ta + 1, na + tb], [ta, ta + 1, na + tb + 1],
        [ta, na + tb, na + tb + 1], [ta + 1, na + tb, na + tb + 1]];
      const v = makeVolume(pts, upT, loT, { bore, seg: i, from: S.from }, (k) => (k < na ? 0 : 1));
      if (v) out.push(v);
    }
    prev = next;
  }
  for (let r = 0; r < S.rooms.length; r++) {
    const f = S.rooms[r], pts = [];
    for (let k = 0; k + 2 < f.length; k += 3) pts.push([f[k], f[k + 1], f[k + 2]]);
    if (pts.length < 4) continue;
    const T = allTriples(pts.length);
    const v = makeVolume(pts, T, T, { bore, seg: -1 - r, from: S.from });
    if (v) out.push(v);
  }
  return out;
}

// signed depth of (x, y, z) inside volume v: > 0 inside (the distance to
// its nearest face), <= 0 outside
function depthIn(v, x, y, z) {
  const P = v.P;
  let d = Infinity;
  for (let k = 0; k < v.nk; k++) {
    const f = P[4 * k] * x + P[4 * k + 1] * z + P[4 * k + 2] * y + P[4 * k + 3];
    if (-f < d) d = -f;
  }
  return d;
}
// plan depth: the footprint half-spaces only
function depthInPlan(v, x, z) {
  const P = v.P;
  let d = Infinity;
  for (const k of v.vk) {
    const f = P[4 * k] * x + P[4 * k + 1] * z + P[4 * k + 3];
    if (-f < d) d = -f;
  }
  return d;
}

// the union of a bore's volumes (or any list): max depth over them, > 0 =
// inside some volume; an empty list is -Infinity (outside). The drawn cut
// removes every volume grown by CLEAR_M: outside the drawn cut <=> depth <= -CLEAR_M.
function depthInAll(vols, x, y, z) {
  let d = -Infinity;
  for (const v of vols) { const q = depthIn(v, x, y, z); if (q > d) d = q; }
  return d;
}
// the same over the plan footprints only
function depthInPlanAll(vols, x, z) {
  let d = -Infinity;
  for (const v of vols) { const q = depthInPlan(v, x, z); if (q > d) d = q; }
  return d;
}

module.exports = {
  FLOOR_UNDER_M, CLEAR_M, stationNormals, boreStations, boreVolumes, depthIn, depthInPlan, depthInAll, depthInPlanAll,
};
